import z from 'zod'
import type { RestApiClient } from '../utils/createRestApiClient'
import { createSodexArrayEnvelopeSchema } from './sodexEnvelopeSchema'

const spotSymbolSchema = z.object({
  name: z.string(),
  baseCoin: z.string().optional(),
  quoteCoin: z.string().optional(),
  displayName: z.string().optional(),
})

const perpsSymbolSchema = z.object({
  name: z.string(),
  baseCoin: z.string(),
  quoteCoin: z.string(),
})

export type SymbolPair = {
  readonly base: string
  readonly quote: string
}

/**
 * 現物シンボル名から base / quote の対応表を取得する
 * @param params.client Sodex 現物 REST クライアント
 * @returns シンボル名をキーとした base / quote マップ
 */
export const fetchSpotSymbolMap = async (params: {
  client: RestApiClient
}): Promise<Map<string, SymbolPair>> => {
  const { client } = params
  const symbols =
    (await client.run({
      method: 'GET',
      path: '/markets/symbols',
      responseSchema: createSodexArrayEnvelopeSchema(spotSymbolSchema),
    })) ?? []

  const map = new Map<string, SymbolPair>()
  for (const symbol of symbols) {
    const pair = resolveSymbolPair({
      name: symbol.name,
      baseCoin: symbol.baseCoin,
      quoteCoin: symbol.quoteCoin,
      displayName: symbol.displayName,
    })
    if (pair) {
      map.set(symbol.name, pair)
    }
  }
  return map
}

/**
 * 先物シンボル名から base / quote の対応表を取得する
 * @param params.client Sodex 先物 REST クライアント
 * @returns シンボル名をキーとした base / quote マップ
 */
export const fetchPerpsSymbolMap = async (params: {
  client: RestApiClient
}): Promise<Map<string, SymbolPair>> => {
  const { client } = params
  const symbols =
    (await client.run({
      method: 'GET',
      path: '/markets/symbols',
      responseSchema: createSodexArrayEnvelopeSchema(perpsSymbolSchema),
    })) ?? []

  const map = new Map<string, SymbolPair>()
  for (const symbol of symbols) {
    map.set(symbol.name, {
      base: symbol.baseCoin,
      quote: symbol.quoteCoin,
    })
  }
  return map
}

const resolveSymbolPair = (params: {
  name: string
  baseCoin?: string
  quoteCoin?: string
  displayName?: string
}): SymbolPair | undefined => {
  const { name, baseCoin, quoteCoin, displayName } = params
  if (baseCoin && quoteCoin) {
    return { base: baseCoin, quote: quoteCoin }
  }
  if (displayName?.includes('/')) {
    const [base, quote] = displayName.split('/')
    if (base && quote) {
      return { base, quote }
    }
  }
  if (name.includes('_')) {
    const [base, quote] = name.split('_')
    if (base && quote) {
      return { base, quote }
    }
  }
  if (name.includes('-')) {
    const [base, quote] = name.split('-')
    if (base && quote) {
      return { base, quote }
    }
  }
  return undefined
}
